/**
 * Single source of truth for how a person's role surfaces in the UI.
 *
 * Why this exists:
 *   - In Odyssey, `role` on the user doc is one of:
 *       super_admin · manager · coordinator · employee
 *   - But some users with `role==="employee"` are actually "indirect
 *     managers" / team leads — i.e. they have at least one active direct
 *     report (someone with `manager_id === me.user_id`). Showing those
 *     people a plain "Employee" badge undersells them and erodes trust in
 *     the system. We display them as **"Lead"** instead, optionally
 *     suffixed with their designation set in Control Panel
 *     (e.g. "Lead · Senior Engineer").
 *
 * Inputs:
 *   - `user`            the person being labelled (their own doc, not the
 *                       viewer's). Should at minimum have `role`. Optional:
 *                       `has_reports` (set by /api/auth/me for the viewer),
 *                       `designation` (set in Control Panel).
 *   - `allUsers`        OPTIONAL — full user list. If provided we derive
 *                       has_reports client-side for any user lacking it
 *                       (e.g. rows from /api/users which don't carry that
 *                       flag). Falsy → we only trust `user.has_reports`.
 *
 * Returns: { label, sublabel?, cls } — drop straight into a pill.
 */

const BADGE_CLASSES = {
    super_admin: "bg-purple-100 text-purple-800 border-purple-200",
    manager: "bg-blue-100 text-blue-800 border-blue-200",
    coordinator: "bg-orange-100 text-orange-800 border-orange-200",
    lead: "bg-teal-50 text-[#1F7F79] border-teal-200",
    employee: "bg-slate-100 text-slate-700 border-slate-200",
};

export const getRoleDisplay = (user, allUsers = null) => {
    if (!user) {
        return { label: "Employee", cls: BADGE_CLASSES.employee };
    }
    const role = user.role || "employee";
    const designation = (user.designation || "").trim();
    if (role === "super_admin") {
        return { label: "Super Admin", cls: BADGE_CLASSES.super_admin };
    }
    if (role === "manager") {
        return {
            label: "Manager",
            sublabel: designation || undefined,
            cls: BADGE_CLASSES.manager,
        };
    }
    if (role === "coordinator") {
        return {
            label: "Coordinator",
            sublabel: designation || undefined,
            cls: BADGE_CLASSES.coordinator,
        };
    }
    // role === "employee"
    let hasReports = !!user.has_reports;
    if (!hasReports && Array.isArray(allUsers) && user.user_id) {
        hasReports = allUsers.some(
            (u) => u && u.manager_id === user.user_id && u.active !== false,
        );
    }
    if (hasReports) {
        return {
            label: "Lead",
            sublabel: designation || undefined,
            cls: BADGE_CLASSES.lead,
        };
    }
    return {
        label: "Employee",
        sublabel: designation || undefined,
        cls: BADGE_CLASSES.employee,
    };
};

/** Convenience: render a single compact string like "Lead · Senior Engineer". */
export const getRoleLine = (user, allUsers = null) => {
    const r = getRoleDisplay(user, allUsers);
    return r.sublabel ? `${r.label} · ${r.sublabel}` : r.label;
};
